import {Dialog} from '@bosch/react-frok';
import React, {useContext, useEffect, useState} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import {
    ActionButton,
    ContentWrapper,
    DialogEditActionsWrapper,
    HeaderWarning,
    HeaderWarningDetails,
    TextWrapper
} from './FileMetaDataDialog.style';
import {t} from 'i18next';
import {
    checkFiletype,
    getDateStart,
    getEffectiveDate,
    getFontSize,
    isNullOrEmpty,
    isValidBoolean,
    isValidStringArray,
    isValidText,
    isWatermarkApplicable,
    validateThumbnailImage
} from '../../../utils';
import {useGetThumbnailFile} from '../../../hooks/UseGetThumbnailFile';
import {ObjectType} from '../../../models/ObjectType';
import {StorageObject} from '../../../models/StorageObject';
import {Label} from '../../../models/Label';
import {
    getLabelEntryMapDefault,
    getLabelMapKeys,
    getLabelsAsObjectMap,
    getStorageObjectFullyConfigured,
    isValidStorageObjectCommons,
    setLabelEntryMap
} from '../../../utils/StorageObjectUtils';
import {getEmptyStorageObjectDTO} from '../../../utils/DtoUtils';
import EventEmitter from 'events';
import {
    FileMetaDataDialog_changeComment,
    FileMetaDataDialog_changeDateValidFrom,
    FileMetaDataDialog_changeDateValidTo,
    FileMetaDataDialog_changeDisclaimer,
    FileMetaDataDialog_changeImage,
    FileMetaDataDialog_changeLabels,
    FileMetaDataDialog_changeLocales,
    FileMetaDataDialog_changeOwnerRoles,
    FileMetaDataDialog_changePublicAccess,
    FileMetaDataDialog_changeRoles,
    FileMetaDataDialog_changeTags,
    FileMetaDataDialog_changeUserLabels,
    FileMetaDataDialog_changeVersion,
    FileMetaDataDialog_changeWatermark,
    FolderMetaDataTabLocales_changeLocales,
    FolderMetaDataTabMain_changeLabels
} from '../../../init/events';
import {ContentType} from '../../../backend/ContentType';
import {useGetThumbnailUrl} from '../../../hooks/UseGetThumbnailUrl';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {Valid} from '../../validation/Valid';
import {FileMetaDataTabView} from './FileMetaDataTabView';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {getApplicableTemplateMetadataPropertySettingsMap, isMandatoryComponent} from '../../../utils/TemplateUtils';
import {
    STORAGE_OBJECT_OBJECT_METADATA_DISCLAIMER,
    STORAGE_OBJECT_OBJECT_METADATA_THUMBNAIL_URL,
    STORAGE_OBJECT_OBJECT_METADATA_USER_LABELS,
    STORAGE_OBJECT_OBJECT_METADATA_WATERMARK
} from '../../../init/storage_object_constants';
import {AuthContext} from '../../../context/AuthContext';
import {hasWriteOwnerAccess} from '../../../utils/UserUtils';
import {EditFileDialogProps} from '../StorageObjectDialogProps';

const isValidStorageObjectExtended = (storageObject: StorageObject, templatePropertySettings: Map<string, TemplatePropertySettings>): boolean => {
    return (isValidStorageObjectCommons(storageObject, templatePropertySettings)
        && (isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_WATERMARK, templatePropertySettings) ? isValidBoolean(storageObject.metadata.watermark) : true)
        && (isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_USER_LABELS, templatePropertySettings) ? isValidStringArray(storageObject.metadata.userLabels) : true)
        && (isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_DISCLAIMER, templatePropertySettings) ? isValidText(storageObject.metadata.disclaimer) : true)) as boolean;
};

const isValidStorageObjectThumbnail = (thumbnailFile: File | null, templatePropertySettings: Map<string, TemplatePropertySettings>): boolean => {
    return (isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_THUMBNAIL_URL, templatePropertySettings) ? validateThumbnailImage(thumbnailFile) : true) as boolean;
};

const isValidStorageObject = (storageObject: StorageObject, templatePropertySettings: Map<string, TemplatePropertySettings>, thumbnailFile: File | null): boolean => {
    return (isValidStorageObjectCommons(storageObject, templatePropertySettings)
        && isValidStorageObjectExtended(storageObject, templatePropertySettings)
        && isValidStorageObjectThumbnail(thumbnailFile, templatePropertySettings)) as boolean;
};

const isToSaveThumbnail = (thumbnailUrl?: string, fileName?: string, fileContentType?: string): boolean => {
    if ((fileContentType === undefined) || (isNullOrEmpty(fileContentType)) || (fileContentType === ContentType.OCTET_STREAM)) {
        return false;
    }

    if ((fileName === undefined) || (isNullOrEmpty(fileName))) {
        return false;
    }

    if ((thumbnailUrl === undefined) || (isNullOrEmpty(thumbnailUrl))) {
        return true;
    }

    return (thumbnailUrl !== fileName);
};

/**
 * @name FileMetaDataDialog
 * @description
 * The dialog to add the metadata of a newly uploaded or edit an existing file
 */
export const FileMetaDataDialog: React.VFC<EditFileDialogProps> = (props) => {
    const {isAuthenticated, roles} = useContext(AuthContext);
    const [templatePropertySettings] = useState<Map<string, TemplatePropertySettings>>(getApplicableTemplateMetadataPropertySettingsMap(ObjectType.FILE, props.item?.templateId));
    const [storageObjectInput, setStorageObjectInput] = useState<StorageObject>(getStorageObjectFullyConfigured(props, ObjectType.FILE, templatePropertySettings, props.file ?? null));
    const [isValid, setIsValid] = useState<boolean>(isValidStorageObjectExtended(storageObjectInput, templatePropertySettings));
    const [isAuthorizedWriteOwnerAccess, setIsAuthorizedWriteOwnerAccess] = useState<boolean>(hasWriteOwnerAccess(isAuthenticated, roles, storageObjectInput.metadata.ownerRoles, storageObjectInput.metadata.parentOwnerRoles));
    const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
    const [thumbnailFileStatus, setThumbnailFileStatus] = useState<boolean>(false);
    //initial state of objects that have to be reverted on reset event
    const [labelsInitialState] = useState<Map<string, Label>>(cloneDeep(getLabelEntryMapDefault(props.defaultLocale, storageObjectInput.labels)));
    const [versionInitialState] = useState(cloneDeep(storageObjectInput.metadata.version ?? ''));
    const eventEmitter = new EventEmitter();
    eventEmitter.setMaxListeners(100);

    const getThumbnailUrl = useGetThumbnailUrl();
    const getThumbnailFile = useGetThumbnailFile();

    const setExistingThumbnail = async (thumbnailUrl: string) => {
        if (isNullOrEmpty(thumbnailUrl)) {
            return;
        }

        await getThumbnailUrl(thumbnailUrl)
            .then(async (existingThumbnail) => {
                if (!isNullOrEmpty(existingThumbnail)) {
                    await getThumbnailFile(existingThumbnail, existingThumbnail)
                        .then((existingThumbnailFile) => {
                            if (existingThumbnailFile) {
                                setThumbnailFile(existingThumbnailFile);
                            }
                        })
                        .catch();
                }
                setThumbnailFileStatus(true);
            })
            .catch();
    };

    useEffect(() => {
        if ((storageObjectInput.metadata.thumbnailUrl) && (!isNullOrEmpty(storageObjectInput.metadata.thumbnailUrl))) {
            setExistingThumbnail(storageObjectInput.metadata.thumbnailUrl).catch();
        }
    }, []);

    useEffect(() => {
        if ((props.file === undefined) || (!props.file)) {
            return;
        }

        if ((!isValidText(storageObjectInput.id))
            && isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_THUMBNAIL_URL, templatePropertySettings)) {
            if (checkFiletype(props.file.type) === 'image') {
                setThumbnailFile(props.file);
                setThumbnailFileStatus(true);
                return;
            }

            setIsValid(isValidStorageObject(storageObjectInput, templatePropertySettings, thumbnailFile));
        }
    }, [props.file, storageObjectInput, templatePropertySettings]);

    useEffect(() => {
        if (thumbnailFileStatus) {
            setIsValid(isValidStorageObject(storageObjectInput, templatePropertySettings, thumbnailFile));
        }
    }, [thumbnailFile, thumbnailFileStatus]);

    const handleChangeLabels = (labels: Map<string, Label>) => {
        let storageObject = storageObjectInput;
        storageObject.labels = labels;
        setStorageObjectInput(storageObject);
        let locales = getLabelMapKeys(props.defaultLocale, labels);
        eventEmitter.emit(FolderMetaDataTabMain_changeLabels, labels);
        eventEmitter.emit(FolderMetaDataTabLocales_changeLocales, locales);
        setIsValid(isValidStorageObject(storageObjectInput, templatePropertySettings, thumbnailFile));
    };

    const handleLocales = (locales: string[]) => {
        let labels = setLabelEntryMap(locales, storageObjectInput.labels);
        handleChangeLabels(labels);
    };

    const handleChangeVersion = (version: string) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.version = version.trim();
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObjectInput, templatePropertySettings, thumbnailFile));
    };

    const handleCommentInput = (comment: string) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.comment = comment.trim();
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObjectInput, templatePropertySettings, thumbnailFile));
    };

    const handleDateValidFrom = (dateValidFrom?: Date) => {
        if ((dateValidFrom !== undefined) && dateValidFrom) {
            dateValidFrom = getDateStart(dateValidFrom);
        } else {
            dateValidFrom = getEffectiveDate();
        }

        let storageObject = storageObjectInput;
        storageObject.metadata.dateValidFrom = dateValidFrom?.getTime();
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObjectInput, templatePropertySettings, thumbnailFile));
    };

    const handleDateValidTo = (dateValidTo?: Date) => {
        if ((dateValidTo !== undefined) && dateValidTo) {
            dateValidTo = getDateStart(dateValidTo);
        } else {
            dateValidTo = undefined;
        }

        let storageObject = storageObjectInput;
        storageObject.metadata.dateValidTo = dateValidTo?.getTime();
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObjectInput, templatePropertySettings, thumbnailFile));
    };

    const handleTags = (tags: string[]) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.tags = tags;
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObjectInput, templatePropertySettings, thumbnailFile));
    };

    const handleWatermarkInput = (watermark: boolean) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.watermark = watermark;
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObjectInput, templatePropertySettings, thumbnailFile));
    };

    const handleIsPublic = (isPublic: boolean) => {
        let storageObject = storageObjectInput;
        storageObject.isPublic = isPublic;
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObjectInput, templatePropertySettings, thumbnailFile));
    };

    const handleRoles = (userRoles: string[]) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.roles = userRoles;
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObjectInput, templatePropertySettings, thumbnailFile));
    };

    const handleOwnerRoles = (ownerRoles: string[]) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.ownerRoles = ownerRoles;
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObjectInput, templatePropertySettings, thumbnailFile));
        setIsAuthorizedWriteOwnerAccess(hasWriteOwnerAccess(isAuthenticated, roles, storageObject.metadata.ownerRoles, storageObjectInput.metadata.parentOwnerRoles));
    };

    const handleChangeUserLabel = (userLabels: string[]) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.userLabels = userLabels;
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObjectInput, templatePropertySettings, thumbnailFile));
    };

    const handleChangeDisclaimer = (disclaimer: string) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.disclaimer = disclaimer;
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObjectInput, templatePropertySettings, thumbnailFile));
    };

    const handleImage = (imageFile: File | null) => {
        setThumbnailFile(imageFile);
        setThumbnailFileStatus(true);
        setIsValid(isValidStorageObject(storageObjectInput, templatePropertySettings, thumbnailFile));
    };

    const handleSave = (addMore: boolean) => {
        if (!isValid) {
            return;
        }

        let storageObjectDTO = getEmptyStorageObjectDTO();
        let storageObject = cloneDeep(storageObjectInput);
        storageObject.labels = getLabelsAsObjectMap(storageObjectInput.labels);
        let isToSendThumbnailFile = isToSaveThumbnail(storageObject.metadata.thumbnailUrl, thumbnailFile?.name, thumbnailFile?.type);
        if (!thumbnailFile) {
            storageObject.metadata.thumbnailUrl = undefined;
            storageObject.metadata.thumbnailContentType = undefined;
        } else if (isToSendThumbnailFile) {
            storageObject.metadata.thumbnailUrl = thumbnailFile.name;
            storageObject.metadata.thumbnailContentType = thumbnailFile.type;
        }

        storageObjectDTO.item = storageObject;
        props.onSave(storageObjectDTO, (isToSendThumbnailFile ? thumbnailFile : null), addMore);
    };

    eventEmitter.on(FileMetaDataDialog_changeLabels, handleChangeLabels);
    eventEmitter.on(FileMetaDataDialog_changeLocales, handleLocales);
    eventEmitter.on(FileMetaDataDialog_changeVersion, handleChangeVersion);
    eventEmitter.on(FileMetaDataDialog_changeComment, handleCommentInput);
    eventEmitter.on(FileMetaDataDialog_changeDateValidFrom, handleDateValidFrom);
    eventEmitter.on(FileMetaDataDialog_changeDateValidTo, handleDateValidTo);
    eventEmitter.on(FileMetaDataDialog_changeTags, handleTags);
    eventEmitter.on(FileMetaDataDialog_changeWatermark, handleWatermarkInput);
    eventEmitter.on(FileMetaDataDialog_changePublicAccess, handleIsPublic);
    eventEmitter.on(FileMetaDataDialog_changeRoles, handleRoles);
    eventEmitter.on(FileMetaDataDialog_changeOwnerRoles, handleOwnerRoles);
    eventEmitter.on(FileMetaDataDialog_changeUserLabels, handleChangeUserLabel);
    eventEmitter.on(FileMetaDataDialog_changeDisclaimer, handleChangeDisclaimer);
    eventEmitter.on(FileMetaDataDialog_changeImage, handleImage);

    let fontSize = getFontSize([
        t('GENERAL_CANCEL_LABEL'),
        t('GENERAL_SAVE_EXIT_LABEL'),
        t('GENERAL_SAVE_ADD_NEW_LABEL')
    ], 10);

    const actionButtons = () => {
        return (
            <DialogEditActionsWrapper id={generateIdentifier()} className='m-dialog__actions'>
                <ActionButton id={generateIdentifier()}
                              key='cancel'
                              mode='secondary'
                              type='button'
                              title={t('GENERAL_CANCEL_LABEL')}
                              label={t('GENERAL_CANCEL_LABEL')}
                              fontSize={fontSize}
                              onClick={props.onClose}/>
                <ActionButton id={generateIdentifier()}
                              key='saveExit'
                              icon='upload'
                              mode='secondary'
                              type='button'
                              label={t('GENERAL_SAVE_EXIT_LABEL')}
                              title={t('GENERAL_SAVE_EXIT_LABEL')}
                              fontSize={fontSize}
                              disabled={((!isValid) || (!isAuthorizedWriteOwnerAccess))}
                              onClick={() => handleSave(false)}/>
                <ActionButton id={generateIdentifier()}
                              key='saveAddNew'
                              icon='upload'
                              mode='primary'
                              type='button'
                              label={t('GENERAL_SAVE_ADD_NEW_LABEL')}
                              title={t('GENERAL_SAVE_ADD_NEW_LABEL')}
                              fontSize={fontSize}
                              disabled={((!isValid) || (!isAuthorizedWriteOwnerAccess))}
                              onClick={() => handleSave(true)}/>
            </DialogEditActionsWrapper>
        );
    };

    return (
        <Dialog id={generateIdentifier()}
                modal
                open
                title={t(props.item ? 'UPLOAD_FILE_META_DATA_DIALOG_TITLE_EDIT' : 'UPLOAD_FILE_META_DATA_DIALOG_TITLE_UPLOAD')}
                variant={((!isNullOrEmpty(storageObjectInput.id)) && ((props.file !== undefined) && props.file)) ? 'warning' : 'info'}
                onClose={props.onClose}>
            {((!isNullOrEmpty(storageObjectInput.id)) && ((props.file !== undefined) && props.file)) &&
                <div id={generateIdentifier()}>
                    <HeaderWarning id={generateIdentifier()}>
                        {t('UPLOAD_FILE_META_DATA_DIALOG_EDIT_EXISTING_ITEM')}
                    </HeaderWarning>
                    {(!storageObjectInput.toRemove) &&
                        <HeaderWarningDetails id={generateIdentifier()}>
                            {t('UPLOAD_FILE_META_DATA_DIALOG_EDIT_EXISTING_ITEM_DETAILS')}
                        </HeaderWarningDetails>
                    }
                    {storageObjectInput.toRemove &&
                        <HeaderWarningDetails id={generateIdentifier()}>
                            {t('UPLOAD_FILE_META_DATA_DIALOG_EDIT_MARKED_FOR_DELETION_ITEM')}
                        </HeaderWarningDetails>
                    }
                </div>
            }
            <ContentWrapper id={generateIdentifier()}>
                <Valid isValid={isValid}
                       message={t('META_DATA_DIALOG_NOT_VALID_CONSTRAINT')}>
                    <FileMetaDataTabView templatePropertySettings={templatePropertySettings}
                                         item={storageObjectInput}
                                         thumbnailFile={thumbnailFile}
                                         currentPath={props.currentPath}
                                         rolesDataSource={props.rolesDataSource}
                                         parentOwnerRoles={props.parentOwnerRoles}
                                         parentRoles={props.parentRoles}
                                         parentLocales={props.parentLocales}
                                         language={props.language}
                                         defaultLocale={props.defaultLocale}
                                         disclaimerData={props.disclaimerData}
                                         labelData={props.labelData}
                                         isWatermarkApplicable={isWatermarkApplicable(storageObjectInput.metadata.contentType, props.file?.type)}
                                         labelsInitialState={labelsInitialState}
                                         versionInitialState={versionInitialState}
                                         eventEmitter={eventEmitter}/>
                </Valid>
            </ContentWrapper>
            <TextWrapper id={generateIdentifier()}>
                {`* ${t('GENERAL_MANDATORY_FIELDS_INFO_TEXT')}`}
            </TextWrapper>
            {actionButtons()}
        </Dialog>
    );
};
